import React, { useState } from 'react';
import {
	Button,
	BUTTON_KINDS,
	Input,
	InputErrorMessage,
	INPUT_TYPES,
	Popover,
	Text,
	TEXT_KINDS_ALL
} from '@tme/toyota-common';
import styles from './VinCheckerForm.module.scss';
import {
	VinCheckerErrorMessages,
	VinCheckerFormLabels
} from '../../@types/labelTypes';
import { useSetRecoilState } from 'recoil';
import {
	ctpeuResponseStatusCodeAtom,
	featuresAndCapabilitiesAtom,
	vehicleDataLoaderAtom,
	vehicleDetailsAtom
} from '../../store/atoms/vehicleData';
import { aggregatorService } from '../../services/aggregator';
import {
	FeaturesAndCapabilitiesResponsePayloadService,
	VehicleFeatures
} from '../../@types/dataTypes';
import _ from 'lodash';
import { replacePlaceHoldersInLabel, isValidVin } from '../../tools/tools';
import { CONSTANTS } from '../../constants/constants';
import { setAnalyticsHelper } from '../../tools/analyticsHelper';
import { ANALYTICS_CONSTANTS } from '../../constants/analytics';

const {
	STEPS: { TYPE_VIN, VIN_FOUND, ERROR },
	LABELS: {
		ERROR_SYSTEM,
		ERROR_INVALID_VIN,
		ERROR_NON_EXISTING_VIN,
		ERROR_PROACE
	}
} = ANALYTICS_CONSTANTS;

type VinCheckerFormProps = {
	labels: VinCheckerFormLabels;
	errorLabels: VinCheckerErrorMessages;
	setShowDownloadAppCTA: React.Dispatch<React.SetStateAction<boolean>>;
};

const { HTTP_RESPONSE_CODES, HEADING_CLASS } = CONSTANTS;

export default function VinCheckerForm({
	labels,
	errorLabels,
	setShowDownloadAppCTA
}: VinCheckerFormProps): React.JSX.Element {
	const setVehicleDetails = useSetRecoilState(vehicleDetailsAtom);
	const setCtpeuResponseStatusCode = useSetRecoilState(
		ctpeuResponseStatusCodeAtom
	);
	const setFeaturesAndCapabilities = useSetRecoilState<
		FeaturesAndCapabilitiesResponsePayloadService[]
	>(featuresAndCapabilitiesAtom);
	const [value, setValue] = useState<string>(``);
	const [isUserTyped, setUserTyped] = useState<boolean>(false);
	const [showErrorMessage, setShowErrorMessage] = useState<boolean>(false);
	const [getErrorMessage, setErrorMessage] = useState<string>(``);
	const setVehicleDataLoader = useSetRecoilState(vehicleDataLoaderAtom);

	const handleChange = (
		event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
	): void => {
		if (!isUserTyped && event.target.value.length !== value.length) {
			setAnalyticsHelper(TYPE_VIN);
			setUserTyped(true);
		}
		setValue(event.target.value.replace(/\W|_/gi, ``).toUpperCase());
	};

	const resetData = (): void => {
		setFeaturesAndCapabilities([]);
		setVehicleDetails({});
		setCtpeuResponseStatusCode(``);
		setVehicleDataLoader(false);
	};

	const handleErrorMessage = (errorMessage: string, label?: string): void => {
		label && setAnalyticsHelper(ERROR, undefined, label);
		setErrorMessage(errorMessage);
		setShowErrorMessage(true);
		resetData();
		setVehicleDataLoader(false);
	};

	const handleVinErrors = (vin: string): boolean => {
		if (!vin) {
			handleErrorMessage(errorLabels.emptyVin);
			return false;
		}

		if (!isValidVin(vin)) {
			handleErrorMessage(errorLabels.invalidVin, ERROR_INVALID_VIN);
			setAnalyticsHelper(ERROR, undefined, ERROR_INVALID_VIN);
			return false;
		}

		return true;
	};

	const handleResponse = (response: any): boolean => {
		switch (response.status) {
			case HTTP_RESPONSE_CODES.NOT_FOUND:
				handleErrorMessage(
					errorLabels.nonExistingVin,
					ERROR_NON_EXISTING_VIN
				);
				return false;
			case HTTP_RESPONSE_CODES.PROACE_ERROR:
				handleErrorMessage(errorLabels.proaceError, ERROR_PROACE);
				return false;
		}

		const vehicleDetails = response.payload?.vehicleDetails;
		const services = response.payload?.services;

		if (vehicleDetails && services) {
			setAnalyticsHelper(VIN_FOUND, {
				model: _.get(vehicleDetails, `modelName`, ``)
			});
			setVehicleDetails(vehicleDetails);
			setFeaturesAndCapabilities(services);
			setCtpeuResponseStatusCode(`${response.status}`);
			return true;
		}
		handleErrorMessage(errorLabels.fetchingError, ERROR_SYSTEM);
		return false;
	};

	const submitVin = async (): Promise<void> => {
		setVehicleDataLoader(true);
		setShowDownloadAppCTA(true);

		if (!handleVinErrors(value)) {
			setVehicleDataLoader(false);
			return;
		}

		try {
			setShowErrorMessage(false);
			const response =
				await aggregatorService.getVehicleData<VehicleFeatures>(value);
			handleResponse(response);
		} catch (error) {
			handleErrorMessage(errorLabels.fetchingError, ERROR_SYSTEM);
		} finally {
			setVehicleDataLoader(false);
		}
	};

	const handleKeyDown = async (event: React.KeyboardEvent): Promise<void> => {
		if (event.key === `Enter`) {
			await submitVin();
		}
	};

	return (
		<div className={`${styles.formContainer} form`}>
			<h2 className={HEADING_CLASS}>{labels.title}</h2>
			<div
				className={`${styles.textfieldAndButtonContainer} ${styles.fullWidth}`}
			>
				<div className={`${styles.textfieldContainer}`}>
					<Input
						{...{
							type: INPUT_TYPES.TEXT,
							name: `vin-checker-formfield`,
							id: `vin-checker-form`,
							testId: `vin-checker-form-field`,
							errorTestId: `vin-checker-form-field-error`,
							label: labels.placeholder,
							isRequired: true,
							className: `${styles.textfield} ${styles.fullWidth}`,
							value: value,
							onChange: handleChange,
							onKeyDown: (e: React.KeyboardEvent) =>
								handleKeyDown(e),
							maxLength: 17,
							customIcon: (
								<>
									{
										// @ts-expect-error
										<Popover
											{...{
												popoverId: `vin-checker-tooltip`,
												testId: `vin-checker-tooltip`,
												iconName: `icon-Info-small`,
												iconClassName: `${styles.vinCheckerInputTooltipIcon}`,
												iconKind: `dxp-icon`,
												placement: `bottom`
											}}
										>
											<div
												className={
													styles.vinCheckerTooltipContent
												}
											>
												<Text
													{...{
														kind: TEXT_KINDS_ALL.SPAN,
														testId: `vin-checker-tooltip-title`,
														className:
															styles.vinCheckerTooltipTitle
													}}
												>
													{_.toUpper(
														labels.tooltipTitle
													)}
												</Text>
												<Text
													{...{
														kind: TEXT_KINDS_ALL.SPAN,
														testId: `vin-checker-tooltip-content`,
														className:
															styles.vinCheckerTooltipDescription
													}}
												>
													{labels.tooltip}
												</Text>
											</div>
										</Popover>
									}
								</>
							)
						}}
					/>
				</div>
				<div className={styles.primaryCtaContainer}>
					<Button
						{...{
							kind: BUTTON_KINDS.PRIMARY,
							testId: `vin-checker-primary-cta`,
							label: labels.primaryCta,
							classNames: `${styles.primaryCta} ${styles.fullWidth}`,
							onClick: submitVin
						}}
						aria-label={labels.primaryCta}
					/>
				</div>
			</div>
			{showErrorMessage && (
				<div className={`${styles.noteContainer}`}>
					<InputErrorMessage
						{...{
							errorMessage: getErrorMessage,
							testId: `invalid-vin-error-message`,
							showErrorIcon: false
						}}
					/>
					<div className={styles.emptyDiv} />
				</div>
			)}

			<div />
			<div className={styles.noteContainer}>
				<p className={`${styles.note}`}>
					{replacePlaceHoldersInLabel(`*${labels.note}`)}
				</p>
				<div className={styles.emptyDiv} />
			</div>
		</div>
	);
}
